import React, { useRef } from 'react';
import { Text, Checkbox } from '@cuemath/web-comps';
import {
  Table as SemanticTable,
  TableHeader,
  TableHeaderCell,
  TableCell as SemanticTableCell,
  Loader,
} from 'semantic-ui-react';
import styled, { css, keyframes } from 'styled-components';

import InfoIcon from 'img/info.svg';
import { ReactComponent as FilterSvgs } from 'img/filter.svg';
import { COLORS, Row } from '@cuemath/comps';
import Popup from 'reactjs-popup';

const Root = styled.div`
  opacity: 1;
  border-top: 1px solid ${COLORS.GHOST};
  border-radius: 4px;
  ::-webkit-scrollbar {
    width: 0; /* Remove scrollbar space */
    height: 0;
    background: transparent; /* Optional: just make scrollbar invisible */
  }
  flex-basis: ${({ flexBasis }) => flexBasis || 'auto'};
  ${({ havePagination, scrollBodyHeight }) =>
    (havePagination || scrollBodyHeight) &&
    css`
      position: relative;
      height: ${({ scrollBodyHeight }) => scrollBodyHeight || '532px'};
      overflow: auto;
      border: 1px solid ${COLORS.GHOST};
    `};
`;

const CustomTable = styled(SemanticTable)`
  border-top: none !important;
`;

const HeaderRow = styled(TableHeader)`
  position: sticky;
  top: 0;
  z-index: 8;
`;

const HeaderCell = styled(TableHeaderCell)`
  height: 36px;
  background: ${COLORS.CLOUD} !important;
  padding: 8px 12px !important;
  :hover {
    cursor: ${({ isFunction }) => isFunction && 'pointer'}!important;
  }
`;

const CapitalizedText = styled(Text)`
  text-transform: capitalize;
`;

const InfoCellIcon = styled.img`
  cursor: pointer;
`;

const InfoIconWrapper = styled.div`
  display: flex;
  height: 100%;
  width: 100%;
  justify-content: center;
  align-items: center;
`;

const BodyWrapper = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`;

const InfoHeaderCell = styled(TableHeaderCell)`
  background: ${COLORS.CLOUD} !important;
  padding: 4px 12px !important;
`;

const TableCellStyled = styled(SemanticTableCell)`
  height: 36px;
  padding: 4px 12px !important;
  :hover {
    cursor: ${({ isFunction }) => isFunction && 'pointer'};
    text-decoration: ${({ isFunction }) => isFunction && 'underline'};
  }
`;

const FilterIcon = styled(FilterSvgs)`
  margin: auto 4px;
  path {
    fill: ${({ filterApplied }) => filterApplied && COLORS.COLA};
  }
`;

const TableText = styled(Text)`
  overflow-wrap: break-word;
  text-transform: ${({ textTransform }) => textTransform};
`;

const FadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const TableBodyWrapper = styled(SemanticTable.Body)`
  animation: ${FadeIn} 0.5s ease-in-out;
`;

const TableNoDataText = styled(Text)`
  text-align: center;
`;

const CheckBoxWrapper = styled(Row)`
  padding: 0;
  cursor: pointer;
`;

const CheckboxInput = styled.div`
  pointer-events: none;
`;

const renderTableBody = ({ showLoader, dataLength, children, noDataMessage }) => {
  if (showLoader) {
    return (
      <BodyWrapper>
        <Loader inline active />
      </BodyWrapper>
    );
  }

  if (dataLength === 0) {
    return (
      <BodyWrapper>
        <TableNoDataText variant="body2">
          {noDataMessage || 'No Data Found'}
        </TableNoDataText>
      </BodyWrapper>
    );
  }

  return <TableBodyWrapper>{children}</TableBodyWrapper>;
};

export default function Table({
  havePagination,
  children,
  headerCell,
  selectable,
  showInfo,
  infoWidth,
  showLoader,
  dataLength,
  flexBasis,
  noDataMessage,
  scrollBodyHeight,
}) {
  const leadPopupRef = useRef('');

  return (
    <Root
      havePagination={havePagination || showLoader || dataLength === 0}
      flexBasis={flexBasis}
      scrollBodyHeight={scrollBodyHeight}
    >
      <CustomTable celled selectable={Boolean(selectable)} size="small">
        <HeaderRow>
          <SemanticTable.Row>
            {showInfo === false ? null : (
              <InfoHeaderCell>
                <Text width={infoWidth} variant="body3">
                  Info
                </Text>
              </InfoHeaderCell>
            )}
            {headerCell?.map(
              (
                {
                  title,
                  filterComponent,
                  filterApplied,
                  showFilters,
                  onClick,
                  checkSelectData,
                },
                index,
              ) => {
                const { checkbox, checked, onSelection } = checkSelectData || {};
                const firstElement = index === 0;
                const lastElement = index === headerCell.length - 1;
                const filterPosition = firstElement
                  ? 'bottom left'
                  : lastElement
                  ? 'bottom right'
                  : 'bottom center';

                return (
                  <HeaderCell
                    isFunction={typeof onClick === 'function'}
                    onClick={onClick}
                    key={title}
                  >
                    <Row alignItems="center" justifyContent="center">
                      <CapitalizedText variant="body3">{title}</CapitalizedText>
                      {showFilters && (
                        <Popup
                          key={title}
                          ref={leadPopupRef}
                          trigger={<FilterIcon filterApplied={filterApplied} />}
                          offsetY={10}
                          offsetX={5}
                          position={filterPosition}
                          on="click"
                          closeOnDocumentClick
                          contentStyle={{
                            position: 'absolute',
                            width: '200px',
                          }}
                          arrow={false}
                        >
                          {filterComponent}
                        </Popup>
                      )}
                      {checkbox && (
                        <CheckBoxWrapper>
                          <Checkbox
                            checked={checked}
                            onChange={typeof onSelection === 'function' && onSelection}
                          />
                        </CheckBoxWrapper>
                      )}
                    </Row>
                  </HeaderCell>
                );
              },
            )}
          </SemanticTable.Row>
        </HeaderRow>
        {/* <SemanticTable.Body> */}
        {renderTableBody({ showLoader, dataLength, children, noDataMessage })}
        {/* </SemanticTable.Body> */}
      </CustomTable>
    </Root>
  );
}

export function TableInfoCell({ onClick, width }) {
  return (
    <TableCellStyled width={width} verticalAlign="bottom" textAlign="center">
      <InfoIconWrapper>
        <InfoCellIcon onClick={onClick} src={InfoIcon} alt="info" />
      </InfoIconWrapper>
    </TableCellStyled>
  );
}

export function TableCell({
  children,
  width,
  onClick,
  textTransform = 'capitalize',
  color,
}) {
  return (
    <TableCellStyled
      width={width}
      onClick={onClick}
      isFunction={typeof onClick === 'function'}
      verticalAlign="middle"
    >
      <TableText color={color} variant="body2" textTransform={textTransform}>
        {children}
      </TableText>
    </TableCellStyled>
  );
}
